import { FLOW_TYPE } from 'consts/flows';
import { getQuestionnaireType, getRiskqSSNPath, IsDisabled, nextBTNLabel, transformRiskq } from 'helpers';
import clamp from 'lodash/clamp';
import { AppContext } from 'providers/AppContext';
import { useContext, useEffect, useState } from 'react';
import { baseAction } from 'store/actions';
import useSWR from 'swr';
import type { Questionnaire } from 'types';

export function useRiskq(testType: 'appropriateness' | 'suitability') {
	const ctx = useContext(AppContext);
	const { config, dispatch, entityType, fetchRelations, flowType, http, state, t, navigate } = ctx;
	const [isDisabled, setIsDisabled] = useState(true);
	const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>();
	const [pageIndex, setPageIndex] = useState(0);
	const { API, IS_EMPLOYEE_WEB } = config;
	const { context, riskq, user } = state;
	const processId = context.processInfo?.agreementProcessId;
	const isRRSU = FLOW_TYPE.RR_SU === flowType;
	const ssn = IS_EMPLOYEE_WEB ? getRiskqSSNPath(isRRSU, context.kennitala, user.kennitala) : '';
	const questionnaireType = getQuestionnaireType(entityType, flowType, testType);
	const url = processId ? `${API.riskq}/${processId}/questionnaire/${questionnaireType}${ssn}` : null;
	const { data } = useSWR<Questionnaire>(url);
	const lastPageIndex = questionnaire?.questionGroups.length ? questionnaire.questionGroups.length - 1 : 0;
	const safePageIndex = questionnaire ? clamp(pageIndex, 0, lastPageIndex) : 0;
	const group = questionnaire?.questionGroups[safePageIndex];
	const title = t(`${flowType}_${entityType}_${testType}_page_${safePageIndex}_title`, 'riskq');
	const prefix = `${flowType}_${entityType}_${testType}_page_${safePageIndex + 1}`;
	const isLastPage = isRRSU ? false : safePageIndex === lastPageIndex;
	const page = `${testType}_${safePageIndex + 1}`;
	const next = nextBTNLabel(flowType, isLastPage, t);
	const prev = safePageIndex === 0 ? undefined : t('back');

	const handlePrev = () => {
		const previousIndex = clamp(safePageIndex - 1, 0, lastPageIndex);
		setPageIndex(previousIndex);
	};

	const handleNext = async () => {
		const nextIndex = safePageIndex + 1;
		if (nextIndex <= lastPageIndex) {
			setPageIndex(nextIndex);
			return;
		}

		if (!url) return;

		try {
			const payload = { answers: riskq };
			await http.post(url, payload);
			dispatch([baseAction, { riskq: [] }]);
			await fetchRelations(true);
		} catch (error) {
			console.error('Risk questionnaire submission failed', error);
		}
	};

	useEffect(() => {
		if (!processId) {
			navigate(`/${flowType}/${entityType}/onboard`);
		}
	}, [processId, entityType, flowType, navigate]);

	useEffect(() => {
		dispatch([baseAction, { riskq: [] }]);
	}, [dispatch]);

	useEffect(() => {
		if (data) {
			setQuestionnaire(transformRiskq(data));
		}
	}, [data]);

	useEffect(() => {
		setIsDisabled(IsDisabled());
	}, [riskq, safePageIndex]);

	useEffect(() => {
		if (questionnaire && pageIndex !== safePageIndex) {
			setPageIndex(safePageIndex);
		}
	}, [questionnaire, pageIndex, safePageIndex]);

	return {
		...ctx,
		data: questionnaire,
		group,
		handleNext,
		handlePrev,
		isDisabled,
		isLastPage,
		next,
		page,
		prefix,
		prev,
		testType,
		title
	};
}
